import Alpine from "alpinejs";
import { observeAppAnalytics } from "./analytics";

/**
 * Module Manager — shows calculator I/O format and config mapping.
 *
 * Tree: SourceModule → outputKeys + inputDescriptions + linked configs (with valueKeys)
 * Test: select employee → run pipeline → see calculator output keys + how configs use them
 */

interface OutputKey {
  key: string;
  label: string;
  description: string;
  unit: string;
}

interface InputDesc {
  unitPrice: { label: string; description: string; defaultValue: number };
  multiplier: { label: string; description: string; defaultValue: number };
}

interface ConfigInfo {
  id: string;
  code: string;
  name: string;
  valueKeys: string[];
  unitPrice?: number;
  multiplier?: number;
  rate?: number;
  isActive: boolean;
  isAutoCalculated?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
  sortOrder: number;
  notes?: string;
  itemCode?: { code: string; name: string } | null;
}

interface RegistryModule {
  key: string;
  name: string;
  type: "allowance" | "deduction" | "both";
  hasCalculator: boolean;
  outputKeys: OutputKey[];
  inputDescriptions: InputDesc | null;
  allowanceConfigs: ConfigInfo[];
  deductionConfigs: ConfigInfo[];
  status: "active" | "configured" | "unimplemented" | "manual";
}

const now = new Date();
const curYear = now.getFullYear();
const curMonth = now.getMonth() + 1;

export function appComponent() {
  return {
    /* ── State ── */
    dataReady: false,
    sdk: Alpine.store("hereby").sdk,

    /* ── Registry Data ── */
    registryModules: [] as RegistryModule[],
    registrySummary: null as Record<string, unknown> | null,
    loadingRegistry: false,
    registryError: null as string | null,

    /* ── Active Module ── */
    activeModule: null as RegistryModule | null,

    /* ── Test Runner ── */
    testYear: curYear,
    testMonth: curMonth,
    testRunning: false,
    testResult: null as Record<string, unknown> | null,
    testError: null as string | null,
    testElapsed: 0,

    /* ── Employee Search (inline) ── */
    empSearchQuery: "",
    empSearchResults: [] as { id: string; name: string; employeeNumber: string; email: string }[],
    empSearching: false,
    empSearchOpen: false,
    empSelectedId: "" as string,
    empMeta: null as Record<string, unknown> | null,

    /* ── JSON Detail ── */
    selectedRow: null as unknown,
    showDetailModal: false,

    /* ── Lifecycle ── */
    async init() {
      observeAppAnalytics(this, () =>
        this.dataReady ? (this.activeModule ? "module_detail" : "registry") : undefined,
      );
      if (this.dataReady) {
        return;
      }
      const store = Alpine.store("hereby");

      while (store.isLoading) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (!store.currentUser) {
        return;
      }

      this.dataReady = true;
      await this.loadRegistry();

      // Restore from URL hash
      const hash = Alpine.store("router").tab;
      if (hash) {
        const found = this.registryModules.find((m: RegistryModule) => m.key === hash);
        if (found) {
          this.selectModule(found);
        }
      }
    },

    /* ── Registry Loading ── */
    async loadRegistry() {
      this.loadingRegistry = true;
      this.registryError = null;
      try {
        const result = await this.sdk.moduleRegistry.list();
        // biome-ignore lint/suspicious/noExplicitAny: API response shape
        const data = result as any;
        this.registryModules = data.modules || [];
        this.registrySummary = data.summary || null;
      } catch (e: unknown) {
        this.registryModules = [];
        this.registrySummary = null;
        this.registryError = e instanceof Error ? e.message : String(e);
      } finally {
        this.loadingRegistry = false;
      }
    },

    /* ── Module Selection ── */
    selectModule(mod: RegistryModule) {
      this.activeModule = mod;
      this.testResult = null;
      this.testError = null;
      Alpine.store("router").navigateTab(mod.key);
    },

    /* ── Employee Search ── */
    async searchEmployees() {
      const q = this.empSearchQuery.trim();
      if (q.length < 1) {
        this.empSearchResults = [];
        return;
      }
      this.empSearching = true;
      try {
        const result = await this.sdk.employee.getEmployees({ search: q, limit: 10 });
        // biome-ignore lint/suspicious/noExplicitAny: API response shape
        const r = result as any;
        const items = Array.isArray(r) ? r : r?.items || r?.data || [];
        // biome-ignore lint/suspicious/noExplicitAny: API response shape
        this.empSearchResults = items.map((e: any) => ({
          id: e.id,
          name: [e.lastName, e.firstName].filter(Boolean).join("") || e.name || e.email,
          employeeNumber: e.employeeNumber || "-",
          email: e.email || "-",
        }));
      } catch {
        this.empSearchResults = [];
      } finally {
        this.empSearching = false;
      }
    },

    onEmpInput() {
      this.empMeta = null;
      this.empSelectedId = "";
      if (this.empSearchQuery) {
        this.searchEmployees();
        this.empSearchOpen = true;
      } else {
        this.empSearchOpen = false;
      }
    },

    async selectEmployee(emp: { id: string; name: string; employeeNumber: string }) {
      this.empSelectedId = emp.id;
      this.empSearchQuery = `${emp.name} (${emp.employeeNumber})`;
      this.empSearchOpen = false;
      this.empSearchResults = [];
      this.testResult = null;
      this.testError = null;

      // Fetch employee meta
      this.empMeta = null;
      try {
        // FIXME(타입 복구 중 발견): getEmployee 응답의 contract 타입에 hobongAmount/longevityAmount/allowances가 없어 직원 메타 표시 필드와 맞지 않음. 응답 DTO 복구 후 any 제거.
        // biome-ignore lint/suspicious/noExplicitAny: SDK response type mismatch during getter migration
        const detail: any = await this.sdk.employee.getEmployee(emp.id);
        this.empMeta = {
          name: detail.fullName || `${detail.lastName}${detail.firstName}`,
          employeeNumber: detail.employeeNumber,
          status: detail.status,
          hireDate: detail.hireDate ? String(detail.hireDate).split("T")[0] : null,
          baseSalary: detail.contract?.baseSalary,
          hourlyRate: detail.contract?.hourlyRate,
          hobongAmount: detail.contract?.hobongAmount,
          longevityAmount: detail.contract?.longevityAmount,
          allowances: detail.contract?.allowances,
        };
      } catch {
        // Silently fail — meta is optional
      }
    },

    /* ── Test: Run pipeline for employee and get calculator output ── */
    async runTest() {
      if (!(this.activeModule && this.empSelectedId)) {
        return;
      }
      this.testRunning = true;
      this.testResult = null;
      this.testError = null;
      const start = performance.now();
      try {
        // Generate payroll for the period (this runs the full pipeline)
        await this.sdk.payroll.generateForPeriod(Number(this.testYear), Number(this.testMonth));

        // Fetch the generated records for this employee
        const mod = this.activeModule;
        if (mod.type === "deduction" || mod.type === "both") {
          const result = await this.sdk.deduction.review(
            Number(this.testYear),
            Number(this.testMonth),
            this.empSelectedId,
          );
          this.testResult = result;
        } else {
          const result = await this.sdk.allowance.list(
            {
              employeeId: this.empSelectedId,
              period: { year: Number(this.testYear), month: Number(this.testMonth) },
            },
            { limit: 100 },
          );
          // For each record, try to get source detail
          // biome-ignore lint/suspicious/noExplicitAny: API response shape
          const items = toArray(result) as any[];
          const details = [];
          for (const rec of items) {
            try {
              // FIXME(타입 복구 중 발견): getSourceDetail 응답의 config 타입에 code가 없어 모듈 매핑 표시 필드와 맞지 않음. 응답 DTO 복구 후 any 제거.
              // biome-ignore lint/suspicious/noExplicitAny: SDK response type mismatch during getter migration
              const detail: any = await this.sdk.allowance.getSourceDetail(rec.id);
              details.push({
                configName: detail?.config?.name || rec.reason,
                configCode: detail?.config?.code,
                sourceModule: detail?.sourceModule,
                valueKeys: detail?.config?.valueKeys,
                recordAmount: rec.amount,
                sourceData: detail?.sourceData,
              });
            } catch {
              details.push({
                configName: rec.reason,
                recordAmount: rec.amount,
                sourceModule: "unknown",
              });
            }
          }
          this.testResult = { items: details, totalRecords: items.length };
        }
        this.testElapsed = Math.round(performance.now() - start);
      } catch (e: unknown) {
        this.testElapsed = Math.round(performance.now() - start);
        this.testError = e instanceof Error ? e.message : String(e);
      } finally {
        this.testRunning = false;
      }
    },

    /* ── Which output keys are used by which configs ── */
    getKeyUsage(key: string): ConfigInfo[] {
      if (!this.activeModule) {
        return [];
      }
      const allConfigs = [
        ...this.activeModule.allowanceConfigs,
        ...this.activeModule.deductionConfigs,
      ];
      return allConfigs.filter((c: ConfigInfo) => c.valueKeys?.includes(key));
    },

    /* ── Detail Modal ── */
    openDetail(row: unknown) {
      this.selectedRow = row;
      this.showDetailModal = true;
    },

    /* ── Helpers ── */
    statusBadgeClass(status: string): string {
      switch (status) {
        case "active":
          return "bg-green-100 text-green-700";
        case "configured":
          return "bg-yellow-100 text-yellow-700";
        case "unimplemented":
          return "bg-red-100 text-red-700";
        default:
          return "bg-gray-100 text-gray-600";
      }
    },

    statusLabel(status: string): string {
      switch (status) {
        case "active":
          return "Active";
        case "configured":
          return "No Configs";
        case "unimplemented":
          return "미구현";
        default:
          return "Manual";
      }
    },

    formatJson(obj: unknown): string {
      try {
        return JSON.stringify(obj, null, 2);
      } catch {
        return String(obj);
      }
    },

    fmt(n: number | null | undefined): string {
      if (n == null || Number.isNaN(n)) {
        return "-";
      }
      return Number(n).toLocaleString("ko-KR");
    },

    doLogout() {
      Alpine.store("hereby").logout();
    },
  };
}

/** Normalize API response to array */
// biome-ignore lint/suspicious/noExplicitAny: API response shapes vary
function toArray(result: unknown): any[] {
  if (Array.isArray(result)) {
    return result;
  }
  if (result && typeof result === "object") {
    // biome-ignore lint/suspicious/noExplicitAny: API response shape varies
    const obj = result as any;
    if (Array.isArray(obj.items)) {
      return obj.items;
    }
    if (Array.isArray(obj.data)) {
      return obj.data;
    }
  }
  return [];
}
